<template>
  <v-dialog :value="value" max-width="460" persistent @keydown.esc="close">
    <v-card>
      <v-card-title class="py-2">
        <v-icon left>mdi-cash-register</v-icon>{{ use_language.make_payment }}
        <v-spacer />
        <v-btn icon small @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />

      <div class="pay-total d-flex align-center justify-space-between px-4 py-3">
        <span>{{ use_language.amount_due }}</span>
        <span class="pay-total-amount">{{ formatPrice(total) }} ฿</span>
      </div>
      <v-divider />

      <v-card-text class="pt-4">
        <div class="pay-methods mb-3">
          <v-card
            v-for="m in methods"
            :key="m.key"
            outlined
            class="pay-method"
            :class="{ 'pay-method--active': method === m.key }"
            @click="method = m.key"
          >
            <div class="text-center py-3 px-1">
              <v-icon size="28" :color="method === m.key ? 'primary' : 'grey darken-1'">
                {{ m.icon }}
              </v-icon>
              <div class="pay-method__label mt-1">{{ m.label }}</div>
            </div>
          </v-card>
        </div>

        <!-- เงินสด: รับเงินมาเท่าไหร่ / ทอนเท่าไหร่ -->
        <template v-if="method === 'cash'">
          <v-text-field
            ref="cashField"
            v-model="received"
            type="number"
            min="0"
            :label="use_language.money_received"
            outlined
            dense
            hide-details
            class="pay-amount"
            @keydown.enter.prevent="confirm"
          />

          <div class="d-flex flex-wrap mt-2">
            <v-btn
              v-for="a in quickAmounts"
              :key="a"
              x-small
              outlined
              class="mr-1 mb-1"
              @click="received = String(a)"
            >
              {{ a === exactAmount ? use_language.exact_amount : formatInt(a) }}
            </v-btn>
          </div>

          <div class="numpad mt-3">
            <v-btn
              v-for="k in numKeys"
              :key="k.v"
              outlined
              class="numpad__key"
              :class="{ 'numpad__key--fn': k.fn }"
              @click="numTap(k.v)"
            >
              <v-icon v-if="k.icon">{{ k.icon }}</v-icon>
              <span v-else>{{ k.v }}</span>
            </v-btn>
          </div>

          <div
            class="d-flex align-center justify-space-between mt-4 pay-change"
            :class="{ 'pay-change--short': shortfall > 0 }"
          >
            <span>{{ shortfall > 0 ? use_language.still_short : use_language.change_money }}</span>
            <span class="pay-change-amount">
              {{ formatPrice(shortfall > 0 ? shortfall : change) }} ฿
            </span>
          </div>
        </template>

        <!-- โอน / บัตรเครดิต: เลขที่อ้างอิงจากเครื่องรูดบัตรหรือสลิป -->
        <template v-else>
          <v-text-field
            ref="refField"
            v-model="referenceNumber"
            :label="use_language.reference_no_label"
            :hint="method === 'credit'
              ? use_language.card_reader_ref_no + ' (' + use_language.optional + ')'
              : use_language.transfer_slip_ref_no + ' (' + use_language.optional + ')'"
            persistent-hint
            outlined
            dense
            @keydown.enter.prevent="confirm"
          />
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn text @click="close">{{ use_language.cancel }}</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!canConfirm || loading"
          @click="confirm"
        >
          <v-icon left>mdi-check</v-icon>{{ use_language.confirm_payment }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { get_languages } from '@/website/global_function'

const METHODS = [
  { key: 'cash', label: 'เงินสด', icon: 'mdi-cash', remark: 'POS Cash' },
  { key: 'qr', label: 'โอนชำระ', icon: 'mdi-qrcode', remark: 'POS QRCode' },
  { key: 'credit', label: 'บัตรเครดิต', icon: 'mdi-credit-card', remark: 'POS Credit Card' }
]

export default {
  name: 'PosPaymentDialog',
  props: {
    value: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
  },
  data: () => ({
    use_language: {},
    methods: METHODS,
    method: 'cash',
    received: '',
    referenceNumber: '',
    numKeys: [
      { v: '1' }, { v: '2' }, { v: '3' },
      { v: '4' }, { v: '5' }, { v: '6' },
      { v: '7' }, { v: '8' }, { v: '9' },
      { v: 'C', fn: true },
      { v: '0' },
      { v: 'back', fn: true, icon: 'mdi-backspace-outline' }
    ]
  }),
  computed: {
    exactAmount () {
      return Math.ceil(this.total)
    },
    quickAmounts () {
      const t = this.exactAmount
      const set = new Set([t]);
      [20, 50, 100, 500, 1000].forEach(n => set.add(Math.ceil(t / n) * n));
      [100, 500, 1000].forEach(n => { if (n >= t) set.add(n) })
      return Array.from(set).sort((a, b) => a - b).slice(0, 5)
    },
    receivedNum () {
      return Number(this.received || 0)
    },
    change () {
      return Math.max(0, this.receivedNum - this.total)
    },
    shortfall () {
      return Math.max(0, this.total - this.receivedNum)
    },
    canConfirm () {
      if (this.method === 'cash') return this.receivedNum >= this.total
      return true
    }
  },
  watch: {
    value (v) {
      if (!v) return
      this.method = 'cash'
      this.received = ''
      this.referenceNumber = ''
      this.$nextTick(() => {
        if (this.$refs.cashField) this.$refs.cashField.focus()
      })
    },
    method (m) {
      this.$nextTick(() => {
        const el = m === 'cash' ? this.$refs.cashField : this.$refs.refField
        if (el) el.focus()
      })
    }
  },
  async created () {
    this.use_language = await this.get_languages(localStorage.getItem('set_language'))
  },
  methods: {
    get_languages,
    numTap (v) {
      if (v === 'C') {
        this.received = ''
        return
      }
      if (v === 'back') {
        this.received = String(this.received || '').slice(0, -1)
        return
      }
      // ต่อเลขท้าย แล้วตัดศูนย์นำหน้าทิ้ง (กด 0 แล้ว 5 ต้องได้ 5 ไม่ใช่ 05)
      this.received = String(Number(String(this.received || '') + v))
    },
    close () {
      this.$emit('input', false)
      this.$emit('cancel')
    },
    confirm () {
      if (!this.canConfirm || this.loading) return
      const m = METHODS.find(i => i.key === this.method)
      this.$emit('confirm', {
        method: this.method,
        methodLabel: m.label,
        remark: m.remark,
        referenceNumber: this.method === 'cash'
          ? null
          : ((this.referenceNumber || '').trim() || null),
        receivedAmount: this.method === 'cash' ? this.receivedNum : null,
        changeAmount: this.method === 'cash' ? this.change : null
      })
    },
    formatPrice (v) {
      return Number(v || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatInt (v) {
      return Number(v || 0).toLocaleString('th-TH')
    }
  }
}
</script>

<style scoped>
.pay-total {
  background: #fafafa;
}
.pay-total-amount {
  font-size: 20px;
  font-weight: 700;
}
.pay-methods {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.pay-method {
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}
.pay-method__label {
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
}
.pay-method--active {
  border-color: #1976d2 !important;
  border-width: 2px !important;
  background: #e3f2fd;
}
.pay-method--active .pay-method__label {
  color: #1976d2;
  font-weight: 600;
}

.numpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.numpad__key.v-btn {
  height: 46px;
  min-width: 0;
  font-size: 18px;
  font-weight: 600;
}
.numpad__key--fn.v-btn {
  font-size: 15px;
  color: #78909c;
}
.pay-amount >>> input {
  text-align: right;
  font-size: 20px;
  font-weight: 600;
}
.pay-change {
  font-size: 15px;
}
.pay-change-amount {
  font-size: 20px;
  font-weight: 700;
  color: #2e7d32;
}
.pay-change--short .pay-change-amount {
  color: #d50000;
}
</style>
